import React from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiShield,
} from "react-icons/fi";
import {
  AccessLevelName,
  accessLevelClassName,
  accessLevelDisplayName,
} from "./PayoutAccessControlData";
import type { PayoutAccount } from "./payoutData";
import { accountFromApiRecord, userFromApiRecord } from "./api/payoutApiAdapters";
import { payoutApi } from "./api/payoutApiClient";
import {
  compactBody,
  errorMessage,
  isPayoutMockMode,
  stableIdempotencyKey,
  verifyStepUp,
} from "./api/payoutMutationHelpers";
import { usePayoutResource } from "./api/usePayoutResource";
import PayoutAddUser from "./PayoutAddUser";
import { showPayoutNotification } from "./PayoutNotification";

type UserActionRow = {
  initials: string;
  name: string;
  role: string;
  accessLevel: AccessLevelName;
  email: string;
  mobile: string;
  status: string;
  lastLogin: string;
  authentication?: string;
  createdOn?: string;
  payoutAccountIds: string[];
  id?: string;
  accountsAccess?: string;
  employeeId?: string;
  dob?: string;
  gender?: string;
  personalEmail?: string;
  alternateMobile?: string;
  department?: string;
  designation?: string;
  joiningDate?: string;
  manager?: string;
  employmentType?: string;
  workLocation?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    country?: string;
    landmark?: string;
  };
  idProofType?: string;
  idProofNumber?: string;
  bloodGroup?: string;
  emergencyContact?: {
    name?: string;
    mobile?: string;
    relationship?: string;
  };
};

const emptyUserPayoutAccount: PayoutAccount = {
  id: "",
  name: "",
  school: "",
  bank: "",
  logoUrl: "",
  accountNumber: "",
  ifsc: "",
  currency: "INR",
  addedOn: "",
  availableBalance: 0,
  accountBalance: 0,
  fundsOnHold: 0,
  overdraftBalance: 0,
  overdraftLimit: 0,
  lowBalanceThreshold: 0,
  accent: "bg-[#6687FF]",
};

const statusClassName: Record<string, string> = {
  Active: "bg-[#DCFCE7] text-[#16A34A]",
  Inactive: "bg-[#E5E7EB] text-[#505E8E]",
  Suspended: "bg-[#FEE2E2] text-[#991B1B]",
};

const actionCopy: Record<
  string,
  { title: string; subtitle: string; destination: (userId: string) => string }
> = {
  edit: {
    title: "Verify Edit User",
    subtitle: "Verify this request before saving payout user access changes.",
    destination: () => "/payouts/users",
  },
  delete: {
    title: "Verify Delete User",
    subtitle: "Verify this request to finish removing payout user access.",
    destination: (userId) => `/payouts/users/delete/${userId}/complete`,
  },
  "change-password": {
    title: "Verify Password Change",
    subtitle: "Verify this request before updating this user's password.",
    destination: () => "/payouts/users",
  },
};

const getUserRouteKey = (user: UserActionRow) => user.id || user.email;

function useResolvedUser(userId?: string) {
  const location = useLocation();
  const decoded = React.useMemo(() => (userId ? decodeURIComponent(userId) : ""), [userId]);
  const emptyUser = React.useMemo<UserActionRow>(
    () => ({
      initials: "",
      name: "",
      role: "",
      accessLevel: "Observer",
      email: decoded,
      mobile: "",
      status: "",
      lastLogin: "",
      payoutAccountIds: [],
      accountsAccess: "",
      id: decoded,
    }),
    [decoded],
  );
  const stateUser = (location.state as { payoutUser?: UserActionRow } | null)?.payoutUser;
  const stateFallback =
    stateUser && decoded && getUserRouteKey(stateUser) === decoded ? stateUser : null;
  const fallbackUser = React.useMemo<UserActionRow>(
    () =>
      stateFallback
        ? {
            ...emptyUser,
            ...stateFallback,
            id: stateFallback.id || decoded,
          }
        : emptyUser,
    [decoded, emptyUser, stateFallback],
  );
  const resource = usePayoutResource<UserActionRow[]>(
    [fallbackUser] as UserActionRow[],
    (signal) =>
      decoded
        ? payoutApi
            .userDetails(decoded, signal)
            .then((row) => [userFromApiRecord(row, fallbackUser) as UserActionRow])
        : Promise.resolve([]),
    [decoded, fallbackUser],
  );

  return decoded ? resource.data[0] || fallbackUser : null;
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-[#505E8E]">{label}</p>
      <div className="mt-2 text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

const displayValue = (value?: React.ReactNode) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const joinValues = (values: Array<string | undefined>) =>
  values.map((value) => String(value || "").trim()).filter(Boolean).join(", ") || "-";

function DetailSection({
  title,
  fields,
}: {
  title: string;
  fields: Array<{ label: string; value?: React.ReactNode }>;
}) {
  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <h2 className="text-[18px] font-semibold text-[#1B163B]">{title}</h2>
      <div className="mt-5 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {fields.map((field) => (
          <DetailField
            key={field.label}
            label={field.label}
            value={displayValue(field.value)}
          />
        ))}
      </div>
    </section>
  );
}

function PasswordInput({
  label,
  placeholder,
  value,
  onChange,
  error,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  const [visible, setVisible] = React.useState(false);
  return (
    <label className="block">
      <p className="mb-2 text-xs font-semibold text-[#384A7E]">{label}</p>
      <div
        className={
          "flex h-10 items-center rounded-[6px] border bg-white px-3 " +
          (error ? "border-[#EF4444]" : "border-[#E0E5F2]")
        }
      >
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm font-semibold text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
        />
        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          className="ml-2 shrink-0 text-[#505E8E]"
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-xs font-semibold text-[#EF4444]">{error}</p>
      )}
    </label>
  );
}

function UserPageShell({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <Link
            to="/payouts/users"
            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#505E8E]"
          >
            <FiArrowLeft />
            Back to Users
          </Link>
          <h1 className="text-[24px] font-semibold leading-8 text-[#1B163B]">
            {title}
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function PayoutUserDetails() {
  const { userId } = useParams();
  const user = useResolvedUser(userId);
  const accountsResource = usePayoutResource<PayoutAccount[]>(
    [] as PayoutAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((accounts) =>
        accounts.map((account) => accountFromApiRecord(account, emptyUserPayoutAccount)),
      ),
  );

  if (!user) return <Navigate to="/payouts/users" replace />;

  const assignedAccounts = user.accountsAccess
    ? user.accountsAccess.split(",").map((account) => account.trim()).filter(Boolean)
    : user.payoutAccountIds;
  const accountByKey = new Map<string, PayoutAccount>();
  accountsResource.data.forEach((account) => {
    [
      account.id,
      account.name,
      account.accountNumber,
      `${account.bank} **** ${account.accountNumber}`,
    ]
      .filter(Boolean)
      .forEach((key) => accountByKey.set(String(key).trim(), account));
  });
  const assignedAccountRows = assignedAccounts.map((accountKey) => {
    const account = accountByKey.get(accountKey);
    return {
      id: account?.id || accountKey,
      name: account?.name || accountKey,
      bank: account?.bank || "-",
      accountNumber: account?.accountNumber || "-",
      ifsc: account?.ifsc || "-",
      status: account?.status || "-",
      currency: account?.currency || "-",
    };
  });
  const homeAddress = joinValues([
    user.address?.line1,
    user.address?.landmark,
    user.address?.city,
    user.address?.state,
    user.address?.pinCode,
    user.address?.country,
  ]);

  return (
    <UserPageShell
      title="User Details"
      subtitle="View payout access, assigned accounts and activity details."
      action={
        <Link
          to={`/payouts/users/edit/${encodeURIComponent(getUserRouteKey(user))}`}
          className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
          style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
        >
          Edit User
        </Link>
      }
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#A9B2CF1A] text-sm font-semibold text-[#1B163B]">
                {user.initials}
              </span>
              <div>
                <h2 className="text-[18px] font-semibold text-[#1B163B]">
                  {user.name}
                </h2>
                <p className="mt-1 text-sm font-medium text-[#505E8E]">
                  {user.role}
                </p>
              </div>
              <span
                className={
                  "ml-auto rounded-[5px] px-2 py-1 text-xs font-semibold " +
                  statusClassName[user.status]
                }
              >
                {user.status}
              </span>
            </div>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              <DetailField label="Email" value={user.email} />
              <DetailField label="Mobile" value={user.mobile} />
              <DetailField label="Last Login" value={user.lastLogin} />
              <DetailField
                label="Access Level"
                value={
                  <span
                    className={
                      "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                      accessLevelClassName[user.accessLevel]
                    }
                  >
                    {accessLevelDisplayName[user.accessLevel]}
                  </span>
                }
              />
              <DetailField label="Authentication" value={displayValue(user.authentication)} />
              <DetailField label="Created On" value={displayValue(user.createdOn)} />
            </div>
          </section>

          <DetailSection
            title="Personal Information"
            fields={[
              { label: "Full Name", value: user.name },
              { label: "Employee ID", value: user.employeeId },
              { label: "Company Email", value: user.email },
              { label: "Personal Email", value: user.personalEmail },
              { label: "Mobile Number", value: user.mobile },
              { label: "Alternate Mobile Number", value: user.alternateMobile },
              { label: "Date of Birth", value: user.dob },
              { label: "Gender", value: user.gender },
            ]}
          />

          <DetailSection
            title="Work Information"
            fields={[
              { label: "Department", value: user.department },
              { label: "Designation", value: user.designation },
              { label: "Date of Joining", value: user.joiningDate },
              { label: "Manager", value: user.manager },
              { label: "Employment Type", value: user.employmentType },
              { label: "Work Location", value: user.workLocation },
            ]}
          />

          <DetailSection
            title="Contact & Address"
            fields={[
              { label: "Home Address", value: homeAddress },
              { label: "City", value: user.address?.city },
              { label: "State", value: user.address?.state },
              { label: "PIN Code", value: user.address?.pinCode },
              { label: "Country", value: user.address?.country },
              { label: "Landmark", value: user.address?.landmark },
            ]}
          />

          <DetailSection
            title="Identity & Emergency Contact"
            fields={[
              { label: "ID Proof Type", value: user.idProofType },
              { label: "ID Proof Number", value: user.idProofNumber },
              { label: "Blood Group", value: user.bloodGroup },
              { label: "Emergency Contact Name", value: user.emergencyContact?.name },
              { label: "Emergency Contact Number", value: user.emergencyContact?.mobile },
              { label: "Relationship", value: user.emergencyContact?.relationship },
            ]}
          />

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Assigned Payout Accounts
            </h2>
            <div className="mt-4 divide-y divide-[#E0E5F2] rounded-[8px] border border-[#E0E5F2]">
              {assignedAccountRows.length ? assignedAccountRows.map((account) => (
                <div
                  key={account.id}
                  className="grid gap-4 px-4 py-4 text-sm font-semibold text-[#1B163B] md:grid-cols-[minmax(180px,1fr)_minmax(140px,0.8fr)_minmax(140px,0.8fr)_100px_80px]"
                >
                  <span className="min-w-0">
                    <span className="block truncate" title={account.name}>{account.name}</span>
                    <span className="mt-1 block truncate text-xs font-medium text-[#505E8E]" title={account.id}>{account.id}</span>
                  </span>
                  <span className="text-[#505E8E]">{account.bank}</span>
                  <span>
                    {account.accountNumber}
                    {account.ifsc !== "-" && (
                      <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                        IFSC: {account.ifsc}
                      </span>
                    )}
                  </span>
                  <span className="text-[#505E8E]">{account.status}</span>
                  <span className="text-[#505E8E]">{account.currency}</span>
                </div>
              )) : (
                <div className="px-4 py-5 text-sm font-medium text-[#505E8E]">
                  No payout accounts assigned.
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Access Summary
            </h2>
            <div className="mt-5 space-y-4 text-sm font-medium text-[#505E8E]">
              <p className="flex items-center gap-3">
                <FiCheckCircle className="text-[#16A34A]" />
                Can view payout dashboards and statements.
              </p>
              <p className="flex items-center gap-3">
                <FiCheckCircle className="text-[#16A34A]" />
                Access limited to assigned payout accounts.
              </p>
              <p className="flex items-center gap-3">
                <FiShield className="text-[#505E8E]" />
                Role permissions are inherited from access level.
              </p>
            </div>
          </section>
        </aside>
      </div>
    </UserPageShell>
  );
}

export function PayoutEditUser() {
  const { userId } = useParams();
  const user = useResolvedUser(userId);

  if (!user) return <Navigate to="/payouts/users" replace />;

  return <PayoutAddUser mode="edit" initialUser={user} />;
}

export function PayoutUserActionVerify() {
  const navigate = useNavigate();
  const { action, userId } = useParams();
  const user = useResolvedUser(userId);
  const config = action ? actionCopy[action] : undefined;
  const method = "authenticator" as const;
  const [code, setCode] = React.useState<string[]>(Array(6).fill(""));
  const [submitting, setSubmitting] = React.useState(false);
  const [, setSubmitError] = React.useState("");
  const showSubmitError = React.useCallback((message: string) => {
    setSubmitError(message);
    showPayoutNotification({ tone: "error", message });
  }, []);

  if (!user || !config || !userId) return <Navigate to="/payouts/users" replace />;

  const userRouteKey = getUserRouteKey(user);
  const verificationCode = code.join("");
  const instruction = "Enter the 6-digit code from your Google Authenticator app.";

  const updateCode = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
    if (digit) {
      const nextInput = document.getElementById(`user-action-code-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleVerifyAction = async () => {
    if (submitting) return;
    if (verificationCode.length !== 6) {
      showSubmitError(`${config.title}: enter the 6-digit Google Authenticator code to continue.`);
      return;
    }
    setSubmitError("");

    if (isPayoutMockMode()) {
      if (action === "delete") {
        const stored = JSON.parse(localStorage.getItem("payoutDeletedUserEmails") || "[]");
        localStorage.setItem(
          "payoutDeletedUserEmails",
          JSON.stringify(Array.from(new Set([...stored, user.email]))),
        );
      }
      navigate(config.destination(encodeURIComponent(userRouteKey)));
      return;
    }

    setSubmitting(true);
    try {
      const purpose =
        action === "delete"
          ? "DELETE_USER"
          : action === "change-password"
            ? "CHANGE_USER_PASSWORD"
            : "UPDATE_USER";
      const stepUpToken = await verifyStepUp({
        purpose,
        method,
        code: verificationCode,
      });

      if (action === "delete") {
        await payoutApi.deleteUser(
          userRouteKey,
          stableIdempotencyKey("delete-user", userRouteKey),
          stepUpToken,
        );
      } else if (action === "change-password") {
        const pendingPassword =
          sessionStorage.getItem(pendingPasswordKey(userRouteKey)) ?? "";
        if (!pendingPassword.trim()) {
          throw new Error("Password session expired. Go back and enter the password again.");
        }
        await payoutApi.changeUserPassword(
          userRouteKey,
          compactBody({
            new_password: pendingPassword,
          }),
          stableIdempotencyKey("change-user-password", userRouteKey),
          stepUpToken,
        );
        sessionStorage.removeItem(pendingPasswordKey(userRouteKey));
      } else {
        await payoutApi.updateUser(
          userRouteKey,
          compactBody({
            name: user.name,
            email: user.email,
            role_ids: [user.role],
            status: user.status.toUpperCase(),
            funding_account_ids: user.payoutAccountIds,
          }),
          stableIdempotencyKey("update-user", userRouteKey),
          stepUpToken,
        );
      }
      navigate(config.destination(encodeURIComponent(userRouteKey)));
    } catch (error) {
      showSubmitError(errorMessage(error, `${config.title}: unable to complete this user action. Please try again.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <UserPageShell
      title={config.title}
      subtitle={config.subtitle}
      action={
        <div className="flex items-center gap-3">
          <Link
            to="/payouts/users"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={handleVerifyAction}
            disabled={verificationCode.length !== 6 || submitting}
            className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-6 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
          >
            {submitting ? "Processing..." : "Verify & Continue"}
          </button>
        </div>
      }
    >
      <section className="max-w-[860px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-[8px] bg-[#A9B2CF1A] text-xl text-[#1B163B]">
            <FiShield />
          </span>
          <div>
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Verify User Action
            </h2>
            <p className="mt-1 text-sm font-medium text-[#505E8E]">
              {instruction}
            </p>
          </div>
        </div>

        <div className="mt-7">
          <div className="flex items-center gap-4 rounded-[8px] border border-[#1B163B] bg-white p-4 text-left">
            <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#DCFCE7] text-[#16A34A]">
              <FiShield />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[#1B163B]">
                Google Authenticator
              </span>
              <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                Recommended for sensitive user changes
              </span>
            </span>
          </div>
        </div>

        <div className="mt-7">
          <p className="mb-3 text-sm font-semibold text-[#1B163B]">
            Verification Code
          </p>
          <div className="flex flex-wrap gap-3">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`user-action-code-${index}`}
                value={digit}
                onChange={(event) => updateCode(index, event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && !code[index] && index > 0) {
                    document.getElementById(`user-action-code-${index - 1}`)?.focus();
                  }
                }}
                className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                maxLength={1}
              />
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-4">
            <button
              type="button"
              onClick={() => setCode(Array(6).fill(""))}
              className="text-sm font-semibold text-[#1B163B]"
            >
              Resend code
            </button>
          </div>
        </div>
      </section>
    </UserPageShell>
  );
}

export function PayoutDeleteUserConfirm() {
  const { userId } = useParams();
  const user = useResolvedUser(userId);

  if (!user) return <Navigate to="/payouts/users" replace />;

  return (
    <UserPageShell
      title="Delete User"
      subtitle="Review this user before removing payout dashboard access."
      action={
        <Link
          to="/payouts/users"
          className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
        >
          Cancel
        </Link>
      }
    >
      <section className="max-w-[680px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <h2 className="text-[18px] font-semibold text-[#1B163B]">
          Remove {user.name} from payout access?
        </h2>
        <p className="mt-3 text-sm font-medium leading-6 text-[#505E8E]">
          This user will no longer appear in the payout users list and will lose access to assigned payout accounts.
        </p>
        <div className="mt-5 grid gap-4 rounded-[8px] bg-[#A9B2CF1A] p-4 text-sm font-semibold text-[#1B163B] md:grid-cols-2">
          <DetailField label="Email" value={user.email} />
          <DetailField label="Role" value={user.role} />
          <DetailField label="Status" value={user.status} />
          <DetailField label="Assigned Accounts" value={`${user.payoutAccountIds.length} payout accounts`} />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Link
            to="/payouts/users"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
          >
            Keep User
          </Link>
          <Link
            to={`/payouts/users/delete/${encodeURIComponent(getUserRouteKey(user))}/verify`}
            className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
          >
            Continue to Verification
          </Link>
        </div>
      </section>
    </UserPageShell>
  );
}

export function PayoutDeleteUserComplete() {
  return (
    <UserPageShell
      title="User Deleted"
      subtitle="The user has been removed from payout dashboard access."
      action={
        <Link
          to="/payouts/users"
          className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
          style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
        >
          Back to Users
        </Link>
      }
    >
      <section className="max-w-[620px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 text-center new-shadow">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#DCFCE7] text-2xl text-[#16A34A]">
          <FiCheckCircle />
        </span>
        <h2 className="mt-5 text-[20px] font-semibold text-[#1B163B]">
          User access removed successfully
        </h2>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          The user update has been completed for this browser session.
        </p>
      </section>
    </UserPageShell>
  );
}

const pendingPasswordKey = (userKey: string) => `payoutPendingPassword:${userKey}`;

export function PayoutChangeUserPassword() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const user = useResolvedUser(userId);
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [errors, setErrors] = React.useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  if (!user) return <Navigate to="/payouts/users" replace />;

  const continueToVerification = () => {
    const nextErrors: {
      password?: string;
      confirmPassword?: string;
    } = {};

    if (!password.trim()) {
      nextErrors.password = "Enter a new password.";
    } else if (password.length < 8) {
      nextErrors.password = "Password must be at least 8 characters.";
    }

    if (!confirmPassword.trim()) {
      nextErrors.confirmPassword = "Re-enter the new password.";
    } else if (password !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const userKey = getUserRouteKey(user);
    sessionStorage.setItem(pendingPasswordKey(userKey), password);
    navigate(`/payouts/users/change-password/${encodeURIComponent(userKey)}/verify`, {
      state: { userKey },
    });
  };

  return (
    <UserPageShell
      title="Change Password"
      subtitle="Set a new password for this payout dashboard user."
      action={
        <div className="flex items-center gap-3">
          <Link
            to="/payouts/users"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={continueToVerification}
            className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
          >
            Update Password
          </button>
        </div>
      }
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Password Details
          </h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              value={password}
              onChange={(value) => {
                setPassword(value);
                setErrors((current) => ({ ...current, password: undefined }));
              }}
              error={errors.password}
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                setErrors((current) => ({ ...current, confirmPassword: undefined }));
              }}
              error={errors.confirmPassword}
            />
          </div>
          <p className="mt-4 text-xs font-semibold text-[#166534]">
            Use at least 8 characters with uppercase, lowercase, number and special character.
          </p>
        </section>

        <aside className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            User Summary
          </h2>
          <div className="mt-5 flex items-center gap-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#A9B2CF1A] text-sm font-semibold text-[#1B163B]">
              {user.initials}
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1B163B]">{user.name}</p>
              <p className="mt-1 text-xs font-medium text-[#505E8E]">{user.email}</p>
            </div>
          </div>
          <div className="mt-5 rounded-[6px] bg-[#A9B2CF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
            Password changes are static for now and will connect to backend APIs later.
          </div>
        </aside>
      </div>
    </UserPageShell>
  );
}
